import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { modalFocuser } from '../../../components/common/components/Modal';
import { isMobile } from '../../../utils/constants';
import Spinner from '../../common/components/Spinner';
import * as Sentry from '@sentry/react';
import useLogout from '../../common/hooks/useLogout';

interface NavLinkProps {
	closeMobileSideNavMenu?: () => void;
}

const NavLinks = ({ closeMobileSideNavMenu }: NavLinkProps) => {
	const location = useLocation();

	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);

	const [handleUserLogout]: any = useLogout();

	Sentry.startInactiveSpan({ name: 'LogOut' });

	const handleLogout = (event: any) => {
		event.preventDefault();
		handleUserLogout('home');
	};

	const closeMobileMenu = () => {
		if (closeMobileSideNavMenu) {
			closeMobileSideNavMenu();
		}
	};

	return (
		<>
			{isMobile ? (
				<ul>
					<li>
						<NavLink to="/" onClick={closeMobileMenu}>
							Account
						</NavLink>
					</li>
					<li>
						<NavLink to="/payment-methods" onClick={closeMobileMenu}>
							Payment Methods
						</NavLink>
					</li>
					<li>
						<NavLink to="/my-subscriptions" onClick={closeMobileMenu}>
							Subscriptions & Purchases
						</NavLink>
					</li>
					<li>
						<NavLink to="/devices" onClick={closeMobileMenu}>
							TVs & Sound Bars
						</NavLink>
					</li>
					<li>
						<NavLink to="/logout" onClick={handleLogout}>
							Sign Out
						</NavLink>
					</li>
				</ul>
			) : (
				<ul>
					<li>
						<NavLink to="/">Account</NavLink>
					</li>
					<li>
						<NavLink to="/payment-methods">Payment Methods</NavLink>
					</li>
					<li>
						<NavLink
							to="/my-subscriptions"
							className={({ isActive }) =>
								isActive || location.pathname === '/order-details' ? 'active' : ''
							}>
							Subscriptions & Purchases
						</NavLink>
					</li>
					<li>
						<NavLink to="/devices">TVs & Sound Bars</NavLink>
					</li>
					<li>
						<NavLink to="/logout" onClick={handleLogout}>
							Sign Out
						</NavLink>
					</li>
				</ul>
			)}
			{isSpinnerLoading && <Spinner />}
		</>
	);
};

const SideNav = () => {
	const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
	const [currentPage, setCurrentPage] = useState('Account');
	const sideNavRef: any = useRef();
	const closeButtonRef: any = useRef();
	const location = useLocation();

	const pathTitle = [
		{
			path: '/',
			title: 'Account'
		},
		{
			path: '/payment-methods',
			title: 'Payment Methods'
		},
		{
			path: '/my-subscriptions',
			title: 'Subscriptions & Purchases'
		},
		{
			path: '/devices',
			title: 'TVs & Sound Bars'
		}
	];

	const openAccountMenu = () => {
		const bodyElement: HTMLBodyElement = document.getElementsByTagName('body')[0];
		const scrollPosition = window.scrollY;
		const scrollY = bodyElement.style.top;
		const hideShowElements = document.querySelectorAll(
			'.vce-db-main, .globalnavigation, .emailsignupshortform, .site-footer, .separator'
		);

		if (isAccountMenuOpen) {
			setIsAccountMenuOpen(false);
			bodyElement.classList.remove('acc-select-open');
			bodyElement.removeAttribute('style');
			window.scrollTo(0, parseInt(scrollY || '0') * -1);

			isMobile &&
				hideShowElements.forEach((element) => {
					element.removeAttribute('aria-hidden');
				});
		} else {
			setIsAccountMenuOpen(true);
			bodyElement.classList.add('acc-select-open');
			bodyElement.style.cssText = `overflow: hidden; position: fixed; top: ${-scrollPosition}px`;

			isMobile &&
				hideShowElements.forEach((element) => {
					element.setAttribute('aria-hidden', 'true');
				});
		}
	};

	useEffect(() => {
		if (closeButtonRef.current && isAccountMenuOpen) {
			closeButtonRef.current.focus();
			modalFocuser(sideNavRef.current, 'Account');
		}
	}, [isAccountMenuOpen]);

	useEffect(() => {
		pathTitle.forEach((item: any) => {
			if (item.path === location.pathname) {
				setCurrentPage(item.title);
				const selectMobileMenuItem: any = document.querySelectorAll('.acc-dropdown-content ul li');
				hideMobileMenuItem(selectMobileMenuItem, item.title);
			}
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	const hideMobileMenuItem = (selectMobileMenuItem: any, pageTitle: any) => {
		selectMobileMenuItem.forEach((menuItem: any) => {
			if (menuItem.childNodes[0].innerText === pageTitle) {
				menuItem.style.display = 'none';
			} else {
				menuItem.style.display = 'block';
			}
		});
	};

	const closeMobileMenu = (event: any) => {
		event.stopPropagation();
		if (isAccountMenuOpen) {
			openAccountMenu();
		}
	};

	useEffect(() => {
		document.addEventListener('keyup', function (event) {
			if (event.key === 'Escape') {
				closeMobileMenu(event);
			}
		});

		return () => {
			document.removeEventListener('keyup', closeMobileMenu);
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAccountMenuOpen]);

	const navBar: any = document.querySelector('.nav_bar__inner');
	let navBarHeight = 60;
	if (navBar && navBar.offsetHeight) {
		navBarHeight = navBar.offsetHeight;
	}

	return (
		<div className="dashboardnav">
			<div
				className={`acc-info-dropdown ${isAccountMenuOpen ? 'select-open' : ''}`}
				style={{ top: navBarHeight }}
				id="accInfoDropdown"
				ref={sideNavRef}>
				<div className="acc-info-select">
					<div className="acc-info-container">
						<span className="acc-select-text">{currentPage}</span>
						<button
							className="acc-select-icon"
							aria-expanded={isAccountMenuOpen ? 'true' : 'false'}
							aria-label={isAccountMenuOpen ? 'Close Account menu' : 'Account menu'}
							aria-describedby="menuButtonText"
							onClick={openAccountMenu}
							ref={closeButtonRef}></button>
					</div>
				</div>
				<div className="acc-dropdown-content">
					<NavLinks closeMobileSideNavMenu={openAccountMenu} />
				</div>
			</div>
			{!isMobile && (
				<code id="menuButtonText" className="sr-only" aria-live="assertive">
					{isAccountMenuOpen ? 'expanded' : 'collapsed'}
				</code>
			)}
			<aside className="vce-db-nav">
				<NavLinks />
			</aside>
		</div>
	);
};

export default SideNav;
